import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Bell, Sparkles, GitBranch, Info } from "lucide-react";
import { Link } from "react-router-dom";

const ICON = {
  expert: Sparkles,
  core_update: GitBranch,
  system: Info,
};

const LABEL = {
  expert: "Nouvelle publication expert",
  core_update: "Signal Core Update",
  system: "Information",
};

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}j`;
}

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/alerts");
      setAlerts(data.alerts || []);
      setUnread(data.unread_count || 0);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const openAndMark = async () => {
    setOpen(true);
    if (unread > 0) {
      try {
        await api.post("/alerts/mark-all-read");
        setUnread(0);
        // Update read state locally
        setAlerts((a) => a.map((x) => ({ ...x, read: true })));
      } catch {}
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={openAndMark}
        data-testid="notif-bell-btn"
        className="relative w-9 h-9 flex items-center justify-center border border-white/10 hover:border-white/30 hover:bg-white/[0.03] transition-colors"
      >
        <Bell className="w-4 h-4 text-white/70" />
        {unread > 0 && (
          <span data-testid="notif-badge" className="absolute -top-1 -right-1 min-w-[16px] h-4 bg-[#FF453A] text-black text-[10px] font-mono font-bold flex items-center justify-center px-1">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-11 w-96 max-h-[70vh] overflow-y-auto bg-[#0F0F13] border border-white/10 shadow-2xl z-50" data-testid="notif-dropdown">
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40">Alertes</div>
              <div className="font-display font-bold text-lg">Signaux du jour</div>
            </div>
          </div>
          {alerts.length === 0 ? (
            <div className="p-6 text-sm text-white/50">
              Aucune alerte pour le moment. Le radar tourne toutes les 2h.
            </div>
          ) : (
            <div>
              {alerts.slice(0, 20).map((a) => {
                const Icon = ICON[a.type] || Info;
                return (
                  <a
                    key={a.alert_id}
                    href={a.meta?.url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`notif-item-${a.alert_id}`}
                    className="flex items-start gap-3 p-4 border-b border-white/5 hover:bg-white/[0.03] transition-colors"
                  >
                    <div className={`shrink-0 w-8 h-8 flex items-center justify-center ${a.type === "core_update" ? "bg-[#FF453A]/20 text-[#FF453A]" : "bg-white/10 text-white"}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-1 flex items-center gap-2">
                        <span>{LABEL[a.type] || a.type}</span>
                        <span>·</span>
                        <span>{timeAgo(a.created_at)}</span>
                      </div>
                      <div className="text-sm font-medium text-white/90 line-clamp-2">{a.title}</div>
                      {a.meta?.source && (
                        <div className="text-[11px] text-white/40 mt-1 font-mono">{a.meta.source}</div>
                      )}
                    </div>
                  </a>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
